import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [wishlist, setWishlist] = useState(() => {
    const saved = localStorage.getItem('user_wishlist');
    return saved ? JSON.parse(saved) : [];
  });
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('user_token') || null);
  const [loadingCart, setLoadingCart] = useState(false);
  const [branding, setBranding] = useState({
    brandName: "Himalayannuts",
    tagline: "Pure. Natural. Wholesome.",
    logoUrl: "/images/logo.jpg"
  });

  const wishlistCount = wishlist.length;
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cartItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);

  useEffect(() => {
    localStorage.setItem('user_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    fetch(`${API_URL}/settings/branding`)
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Failed to load branding');
      })
      .then(data => {
        if (data && data.brandName) {
          setBranding(data);
        }
      })
      .catch(err => console.error('Error loading branding:', err));

    if (token) {
      fetch(`${API_URL}/user/auth/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => {
        if (!res.ok) {
          throw new Error('Session expired');
        }
        return res.json();
      })
      .then(data => {
        setUser(data.user);
        fetchCartItems(token);
      })
      .catch(err => {
        console.error(err);
        logout();
      });
    } else {
      setCartItems([]);
      setUser(null);
    }
  }, [token]);

  const fetchCartItems = async (authToken) => {
    setLoadingCart(true);
    try {
      const res = await fetch(`${API_URL}/cart`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCartItems(data);
      }
    } catch (err) {
      console.error('Error fetching cart items:', err);
    } finally {
      setLoadingCart(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('user_token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('user_token');
    setToken(null);
    setUser(null);
    setCartItems([]);
  };

  const handleAddToCart = async (product) => {
    if (!token) {
      window.location.href = '/login';
      return;
    }

    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.product.id === product.id);
      if (existingItem) {
        const newQty = existingItem.quantity + 1;
        syncCartItemWithDb(product.id, newQty);
        return prevItems.map((item) =>
          item.product.id === product.id ? { ...item, quantity: newQty } : item
        );
      }
      syncCartItemWithDb(product.id, 1);
      return [...prevItems, { product, quantity: 1 }];
    });
  };

  const handleUpdateCartQuantity = async (productId, newQuantity) => {
    if (!token) {
      window.location.href = '/login';
      return;
    }

    setCartItems((prevItems) => {
      if (newQuantity <= 0) {
        deleteCartItemFromDb(productId);
        return prevItems.filter((item) => item.product.id !== productId);
      } else {
        syncCartItemWithDb(productId, newQuantity);
        return prevItems.map((item) =>
          item.product.id === productId ? { ...item, quantity: newQuantity } : item
        );
      }
    });
  };

  const handleRemoveFromCart = async (productId) => {
    if (!token) {
      window.location.href = '/login';
      return;
    }

    deleteCartItemFromDb(productId);
    setCartItems((prevItems) => prevItems.filter((item) => item.product.id !== productId));
  };

  const handleClearCart = async () => {
    if (token) {
      try {
        await fetch(`${API_URL}/cart`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Error clearing database cart:', err);
      }
    }
    setCartItems([]);
  };

  const syncCartItemWithDb = async (productId, quantity) => {
    try {
      await fetch(`${API_URL}/cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ productId, quantity })
      });
    } catch (err) {
      console.error('Error syncing cart item with db:', err);
    }
  };

  const deleteCartItemFromDb = async (productId) => {
    try {
      await fetch(`${API_URL}/cart/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Error deleting cart item from db:', err);
    }
  };

  const handleToggleWishlist = (product) => {
    setWishlist((prevWishlist) => {
      const exists = prevWishlist.some((item) => item.id === product.id);
      if (exists) {
        return prevWishlist.filter((item) => item.id !== product.id);
      } else {
        return [...prevWishlist, product];
      }
    });
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      cartCount,
      cartTotal,
      wishlist,
      wishlistCount,
      user,
      token,
      loadingCart,
      branding,
      login,
      logout,
      handleAddToCart,
      handleUpdateCartQuantity,
      handleRemoveFromCart,
      handleClearCart,
      handleToggleWishlist
    }}>
      {children}
    </CartContext.Provider>
  );
};
